import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// CLOCK rs1801260 + CRY1 rs8192440 + PER3 rs57875989 — delayed sleep phase disorder
// misdiagnosed as anxiety-driven insomnia for years.
// The aha: "sleep onset insomnia" is the wrong frame. His clock runs on a 25–26 hour
// period. CBT-i cannot fix a molecular clock. Morning light and phase-timed melatonin can.

export const sleepTariq: Scenario = {
  slug: 'sleep-tariq',
  series: 'Sleep & Circadian',
  title: "He tried everything for insomnia. It was his clock.",
  socialHook:
    "Tariq, 33, was treated for anxiety-driven insomnia. But he was wide awake at midnight and useless at 7am. His results showed the problem was not worry. His body clock was running late.",

  meta: {
    description:
      "A fictional case study: Tariq was treated for insomnia for years, but his sleep pattern fit delayed sleep phase disorder. CLOCK rs1801260, CRY1 rs8192440, PER3 rs57875989, and a DLMO of 11:45pm showed a body clock that runs late and needs timed light, not more sleep hygiene.",
  },

  persona: {
    name: 'Tariq',
    age: 33,
    sex: 'Male',
    ancestry: 'British-Pakistani',
    occupation: 'Management consultant',
    chiefConcern:
      "Tariq cannot fall asleep before 1–2am, no matter how carefully he follows the rules. He completed CBT-i, installed blackout blinds, stopped screens after 9pm, tried magnesium, and took melatonin at bedtime. He was still awake at 2am. A sleep clinic called it sleep-onset insomnia with anxiety. He does not feel anxious. He feels alert at night and barely functional at 7am.",
    familyHistory:
      "Father: a night owl his whole life, has always worked late. Younger brother: similar pattern — never tired before 1am, struggles with early starts.",
  },

  clinicalPicture: {
    symptoms: [
      "Cannot initiate sleep before 1–2am regardless of sleep hygiene compliance",
      "Profound morning fog and inability to function after a 7am alarm",
      "Feels fully alert and mentally sharp from 10pm onwards",
      "Chronic sleep debt from years of forcing an early start against his biological clock",
    ],
    labs: [
      { name: 'DLMO (dim light melatonin onset)', value: '11:45pm', refRange: '9–10pm typical', flag: 'high' },
      { name: 'Ferritin', value: '18 µg/L', refRange: '30–300 µg/L', flag: 'low' },
    ],
    medications: ['No prescription medications'],
    supplements: [
      'Magnesium glycinate 400mg (nightly — self-initiated)',
      'Melatonin 5mg (nightly at bedtime — self-initiated)',
    ],
    lifestyle: [
      'Screen curfew: no screens after 9pm (maintained strictly for 4 months)',
      'Blackout blinds installed, room temperature 18°C',
      'CBT-i program completed — sleep restriction, stimulus control, sleep hygiene all applied',
      'Exercise: 4x/week gym, generally evening sessions after work',
      'Alcohol: 3–4 drinks/week, none after 8pm',
      'Non-smoker',
    ],
  },

  genetics: [
    {
      gene: 'CLOCK',
      variant: 'rs1801260 (T allele)',
      rsid: 'rs1801260',
      genotype: 'T allele — slowed CLOCK feedback loop',
      plainMeaning:
        "Tariq's body clock runs slower than 24 hours — so without the right external reset signals, it drifts later every day.",
      technicalDetail:
        "CLOCK is one of the core molecular clock genes that drives the approximately 24-hour circadian rhythm. The T allele slows the transcription-translation feedback loop that resets the clock each day, effectively lengthening Tariq's circadian period beyond 24 hours. His internal clock drifts later each day unless reset by external time cues. Standard sleep hygiene does not fix a slow molecular clock — it tries to override the output of a mechanism it cannot reach.",
    },
    {
      gene: 'CRY1',
      variant: 'rs8192440 (period-lengthening variant)',
      rsid: 'rs8192440',
      genotype: 'Lengthening variant — extended circadian period',
      plainMeaning:
        "A second variant pushes his internal day even later. His body simply is not ready for sleep at the time most people expect to be tired.",
      technicalDetail:
        "CRY1 is the molecular brake on the CLOCK feedback loop — it suppresses CLOCK/BMAL1 activity and allows the cycle to complete. A period-lengthening variant in CRY1 extends the circadian period further and carries the strongest known genetic association with delayed sleep phase disorder (DSPD). Combined with the CLOCK T allele, Tariq's circadian period is estimated at 25–26 hours. His sleep onset insomnia is not insomnia — it is a clock set to a different timezone. He cannot fall asleep at 11pm for the same reason someone with a normal clock cannot fall asleep at 9pm.",
    },
    {
      gene: 'PER3',
      variant: 'rs57875989 (5-repeat allele)',
      rsid: 'rs57875989',
      genotype: '5-repeat allele — slow sleep pressure accumulation',
      plainMeaning:
        "His version of the PER3 gene means the pressure to sleep builds slowly through the day — so he isn't tired when everyone else is — but once he's sleep-deprived, he crashes harder than most.",
      technicalDetail:
        "PER3 controls the timing of sleep pressure accumulation through the day. The longer 5-repeat allele is associated with slower build-up of homeostatic sleep pressure, which explains why Tariq does not feel tired at 11pm when most people do. The same variant means that when sleep pressure eventually arrives, it hits harder — PER3 5-repeat carriers show steeper cognitive decline on sleep restriction. His terrible mornings after a short, forced sleep are not weakness or low resilience. They are a measurable, predictable biological response to the PER3 genotype interacting with chronic sleep debt.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "He followed the sleep rules. Nothing moved.",
      body: "Tariq has been a late sleeper for as long as he can remember. At university, it barely mattered. In consulting, it did. Early flights, 8am client calls, and breakfast briefings turned his natural rhythm into a daily problem. He tried to fix it the usual way: blackout blinds, no screens after 9pm, no caffeine after 2pm, magnesium, melatonin, and a full CBT-i program. His sleep habits improved, but the real problem did not move. He still could not fall asleep before 1am.",
    },
    {
      type: 'narrative',
      heading: "The problem was timing, not effort",
      body: "A body clock is not a metaphor. It is a real timing system that controls melatonin release, body temperature, alertness, and sleep pressure. In some people, that timing system runs longer than 24 hours and drifts later unless it is reset by morning light. Sleep hygiene helps when the sleep environment is the problem. It cannot reset a clock that is biologically delayed.",
    },
    {
      type: 'reveal',
      heading: "At midnight, his body still thought it was early",
      body: "Tariq's results explain why bedtime felt impossible. Two variants, CLOCK and CRY1, lengthen his internal day to an estimated 25–26 hours. His melatonin test confirms the delay: his body does not begin signaling sleep readiness until 11:45pm. Asking him to sleep at 11pm is like asking someone with a typical clock to sleep at 9pm. It is not a discipline problem. It is a clock running on a later schedule.",
    },
    {
      type: 'reveal',
      heading: "The morning crash was part of the same pattern",
      body: "Tariq's PER3 result helps explain the other half of the story. His pressure to sleep builds slowly, so he is not tired when other people are winding down. But when he is forced awake early after a short night, the cognitive hit is bigger. The fog before 10am is not laziness. It is the cost of forcing a delayed clock into an early schedule.",
    },
    {
      type: 'action',
      heading: "What changes when the target is the clock",
      items: [
        "Use morning bright light therapy: 30 minutes with a 10,000 lux light box within 15 minutes of waking, ideally by 7:30am. Morning light is the main signal that can pull a delayed body clock earlier. Use it daily for at least four weeks before judging the effect.",
        "Move melatonin earlier and lower the dose. For a midnight sleep target, 0.5mg around 6pm is more useful than 5mg at bedtime. The goal is to shift the clock, not sedate him at the wrong time.",
        "In the short term, stop fighting the delayed schedule. Go to bed when genuinely sleepy, wake at a consistent time, and use the light box immediately. The first goal is a stable rhythm, then a gradual shift earlier.",
        "Keep the screen curfew, but understand its role. Screens are not the root cause here. The curfew simply protects the earlier rhythm that morning light and timed melatonin are trying to build.",
        "Discuss delayed sleep phase disorder with a family doctor or sleep specialist. This reframes the problem from anxiety-driven insomnia to a circadian rhythm disorder and supports the right treatment plan and workplace conversations.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Tariq's 14-day circadian reset check-in",
      goalLabel: "Sleep onset time · Morning alertness · Afternoon energy",
      caption:
        "Tariq started light therapy at 7am and low-dose melatonin at 5pm on day 1. Body clocks shift slowly. The first week felt quiet, then sleep timing started moving by day 7.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Sleep onset', value: 2 },
            { label: 'Morning alertness', value: 2 },
            { label: 'Energy by 3pm', value: 4 },
          ],
          note: "Light box on at 7am. Felt nothing different. Fell asleep well after midnight as usual. Morning was the usual fog.",
        },
        {
          day: 2,
          scores: [
            { label: 'Sleep onset', value: 2 },
            { label: 'Morning alertness', value: 2 },
            { label: 'Energy by 3pm', value: 4 },
          ],
          note: "No change yet. Expected this — phase shifting takes days not hours. Keeping consistent.",
        },
        {
          day: 3,
          scores: [
            { label: 'Sleep onset', value: 3 },
            { label: 'Morning alertness', value: 3 },
            { label: 'Energy by 3pm', value: 5 },
          ],
          note: "Asleep slightly earlier than usual — possibly noise. Morning still hard but got through the 8am call.",
        },
        {
          day: 4,
          scores: [
            { label: 'Sleep onset', value: 3 },
            { label: 'Morning alertness', value: 3 },
            { label: 'Energy by 3pm', value: 5 },
          ],
          note: "Consistent slight improvement. Afternoon energy noticeably better. Something is shifting even if mornings are not yet.",
        },
        {
          day: 5,
          scores: [
            { label: 'Sleep onset', value: 4 },
            { label: 'Morning alertness', value: 4 },
            { label: 'Energy by 3pm', value: 6 },
          ],
          note: "Asleep just after 1am. First time in months. Woke at 9am and felt human. This might be real.",
          milestone: "First time asleep by 1am",
        },
        {
          day: 6,
          scores: [
            { label: 'Sleep onset', value: 4 },
            { label: 'Morning alertness', value: 5 },
            { label: 'Energy by 3pm', value: 6 },
          ],
          note: "Saturday — allowed myself to sleep in. Used the light box as soon as I woke anyway. Morning alertness the best it has been in years.",
        },
        {
          day: 7,
          scores: [
            { label: 'Sleep onset', value: 6 },
            { label: 'Morning alertness', value: 6 },
            { label: 'Energy by 3pm', value: 7 },
          ],
          note: "Asleep before midnight. I genuinely cannot remember the last time this happened.",
          milestone: "First time asleep before midnight in 3 years",
        },
        {
          day: 8,
          scores: [
            { label: 'Sleep onset', value: 6 },
            { label: 'Morning alertness', value: 6 },
            { label: 'Energy by 3pm', value: 7 },
          ],
          note: "Consistent midnight sleep. Morning is still not easy but I am functional — that is new.",
        },
        {
          day: 9,
          scores: [
            { label: 'Sleep onset', value: 7 },
            { label: 'Morning alertness', value: 7 },
            { label: 'Energy by 3pm', value: 7 },
          ],
          note: "Earlier than midnight last night. Woke at 7:30am and got up without hitting snooze once. This is not normal for me.",
        },
        {
          day: 10,
          scores: [
            { label: 'Sleep onset', value: 7 },
            { label: 'Morning alertness', value: 7 },
            { label: 'Energy by 3pm', value: 8 },
          ],
          note: "Ten days of light therapy. The shift feels real and building. Afternoon energy higher than it has been since my twenties.",
        },
        {
          day: 11,
          scores: [
            { label: 'Sleep onset', value: 7 },
            { label: 'Morning alertness', value: 7 },
            { label: 'Energy by 3pm', value: 8 },
          ],
          note: "Stable. Starting to plan how to make the 7am light therapy permanent — it is not optional, it is medication.",
        },
        {
          day: 12,
          scores: [
            { label: 'Sleep onset', value: 8 },
            { label: 'Morning alertness', value: 7 },
            { label: 'Energy by 3pm', value: 8 },
          ],
          note: "Woke without an alarm this morning. I am going to be presenting this data at my family doctor appointment next week.",
        },
        {
          day: 13,
          scores: [
            { label: 'Sleep onset', value: 8 },
            { label: 'Morning alertness', value: 8 },
            { label: 'Energy by 3pm', value: 9 },
          ],
          note: "Two good mornings in a row. The DSPD framing matters — I stopped fighting my clock and started working with it.",
        },
        {
          day: 14,
          scores: [
            { label: 'Sleep onset', value: 9 },
            { label: 'Morning alertness', value: 8 },
            { label: 'Energy by 3pm', value: 9 },
          ],
          note: "Consistent midnight sleep, mornings functional. Asking for a DSPD referral. The genetics gave me the frame — I had the right diagnosis all along, just the wrong label.",
          milestone: "Consistent midnight sleep, mornings functional",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'sleep-tariq',
    label: 'Explore your sleep genetics in Coach',
  },
}
